import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  School,
  Users,
  TrendingUp,
  Download,
  CheckCircle,
  Calendar,
  FileText,
  Heart
} from 'lucide-react';

const ForSchools = () => {
  const benefits = [
    "Higher attendance rates - Healthy children learn better and miss fewer school days",
    "Better academic performance - Well children can focus and participate fully in learning",
    "Enhanced school reputation - Parents appreciate schools that care for their children's health",
    "Reduced healthcare-related absences - Early intervention prevents serious illnesses",
    "Access to health education resources - Comprehensive programs for hygiene, nutrition, and wellness",
    "Support for school health initiatives - AMHAS provides materials, training, and ongoing support",
    "Data-driven insights on student health trends - Track improvements and identify areas for focus",
    "AI + telehealth first: vitals & symptoms recorded at school, AI drafts diagnosis, Western doctor reviews; clinics only for procedures"
  ];

  const enrollmentSteps = [
    {
      title: "Enrollment Agreement",
      description: "Sign Memorandum of Understanding outlining enrollment terms, responsibilities, and benefits for your school"
    },
    {
      title: "Student Identification",
      description: "Conduct roll call to identify eligible students (ages 2-18) and gather initial enrollment data"
    },
    {
      title: "Parent Engagement",
      description: "Obtain parent consent and explain AI + telehealth workflow: school vitals → AI pre‑diagnosis → Western doctor review → clinic referral only if procedures are required"
    },
    {
      title: "Premium Collection",
      description: "Process payments ($28 USD / 15,820 CFA / 2,030 GMD per child annually) during designated payment windows with flexible options"
    },
    {
      title: "Health Program Launch",
      description: "Begin health education programs, routine checkups, and set up the school station to collect vitals & symptoms for AI + telehealth"
    }
  ];

  const healthCalendar = [
    { month: "January", activities: ["Annual health checkups", "Health education sessions"] },
    { month: "February", activities: ["Vaccination programs", "Nutrition workshops"] },
    { month: "March", activities: ["Hygiene education", "Water safety training"] },
    { month: "April", activities: ["Growth monitoring", "Health screenings"] },
    { month: "May", activities: ["Mental health awareness", "First aid training"] },
    { month: "June", activities: ["Summer health camp", "Parent health education"] }
  ];

  const downloads = [
    { name: "Enrollment Roster Template", type: "Excel", description: "Template for tracking student enrollments" },
    { name: "Parent Information Posters", type: "PDF", description: "Educational materials for parents" },
    { name: "Health Education Curriculum", type: "PDF", description: "Age-appropriate health lessons" },
    { name: "Parent Consent Forms", type: "PDF", description: "Required forms for enrollment" }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_telehealth_classrom.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">For Schools</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              Become an enrollment hub and improve student health outcomes
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Benefits for Your School</h2>
            <p className="section-subtitle">
              Join hundreds of schools already improving student health and academic performance
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Student Health & Performance</h3>
              <ul className="space-y-4">
                {benefits.slice(0, 3).map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">School Community</h3>
              <ul className="space-y-4">
                {benefits.slice(3).map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enrollment Process */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">How to Enroll Your School</h2>
            <p className="section-subtitle">
              Simple steps to bring AMHAS to your students
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {enrollmentSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-6 text-center"
              >
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Health Calendar */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">School Health Calendar</h2>
            <p className="section-subtitle">
              Year-round health programs and activities for your students
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {healthCalendar.map((month, index) => (
              <motion.div
                key={month.month}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-6"
              >
                <div className="flex items-center mb-4">
                  <Calendar size={20} className="text-primary-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">{month.month}</h3>
                </div>
                <ul className="space-y-2">
                  {month.activities.map((activity, activityIndex) => (
                    <li key={activityIndex} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-accent-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700 text-sm">{activity}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Downloads - Disabled */}
      <section className="section-padding bg-gray-50 opacity-50 pointer-events-none">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title text-gray-400">Resources & Downloads</h2>
            <p className="section-subtitle text-gray-400">
              Everything you need to implement AMHAS at your school
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {downloads.map((download, index) => (
              <motion.div
                key={download.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-6 flex items-center space-x-4 bg-gray-200"
              >
                <div className="w-12 h-12 bg-gray-300 rounded-lg flex items-center justify-center">
                  <Download size={24} className="text-gray-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-400 mb-1">{download.name}</h3>
                  <p className="text-gray-400 text-sm mb-2">{download.description}</p>
                  <span className="text-xs text-gray-400 font-medium">{download.type}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Join hundreds of schools already improving student health and academic performance with AMHAS.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ForSchools;
